import type { LocalDate } from '@js-joda/core';
import { keepPreviousData } from '@tanstack/query-core';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type {
    InstitutionalHolderStatus,
    InstitutionalHoldersResponse,
    InstitutionalHoldersSortBy,
    InstitutionalHoldersSortOrder,
} from '@thinkalpha/quant-ws-client/contracts/institutional-holders.js';
import { ONE_MINUTE } from '@thinkalpha/ui-core/util/timeConstants';
import { getInstitutionalHolders } from 'src/lib/newQuant';

export interface InstitutionalHoldersQueryInput {
    symbol: string;
    asOfDate?: LocalDate;
    status?: InstitutionalHolderStatus;
    search?: string;
    limit?: number;
    skip?: number;
    sortBy?: InstitutionalHoldersSortBy;
    sortOrder?: InstitutionalHoldersSortOrder;
    enabled: boolean;
}

export function getInstitutionalHoldersQuery({
    symbol,
    asOfDate,
    status,
    search,
    limit,
    skip,
    sortBy,
    sortOrder,
    enabled,
}: InstitutionalHoldersQueryInput): QueryObserverOptions<
    InstitutionalHoldersResponse,
    Error,
    InstitutionalHoldersResponse
> {
    const trimmedSearch = search?.trim();
    return {
        queryKey: [
            'institutional-holders',
            symbol,
            asOfDate?.toString() ?? 'latest',
            status ?? 'all',
            trimmedSearch ?? '',
            limit ?? null,
            skip ?? 0,
            sortBy ?? null,
            sortOrder ?? null,
        ] as readonly unknown[],
        queryFn: () =>
            getInstitutionalHolders(symbol, {
                ...(asOfDate !== undefined ? { asOfDate } : {}),
                ...(status !== undefined ? { status } : {}),
                ...(trimmedSearch ? { search: trimmedSearch } : {}),
                ...(limit !== undefined ? { limit } : {}),
                ...(skip !== undefined ? { skip } : {}),
                ...(sortBy !== undefined ? { sortBy } : {}),
                ...(sortOrder !== undefined ? { sortOrder } : {}),
            }),
        enabled: enabled && Boolean(symbol),
        placeholderData: keepPreviousData,
        staleTime: ONE_MINUTE * 5,
    };
}
